import React, { useEffect, useState } from 'react';
import { ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../../../components/Rodal/Rodal';
import { LevelOfService, isSfas } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../../store/selectors';
import { LoadMarketplacePricing } from '../../../../slices/marketplace/pricing/load-marketplace-pricing.thunk';
import createNotification from '../../../../utils/createNotification';
import Helpers from '../../../../utils/helper';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import {
    PricesCalculationHelper,
    ServicePriceDetails,
} from '../../../../utils/pricesCalculationHelper';
interface SuspendEngagementPopupProps {
    isVisible: boolean;
    setIsVisible(value: boolean): void;
    levelOfService: LevelOfService;
    engagementId: number;
    fteDvmNumber: number;
    startLtmRevenue: number;
    // [#1311] The resume's catch-up is running: suspending closes it.
    isTheCatchUpRunning?: boolean;
    fetchData(): void;
}

export const SuspendEngagementPopup = (props: SuspendEngagementPopupProps) => {
    const dispatch = useDispatch() as any;
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const [isLoading, setIsLoading] = useState(false);
    const axios = useStateSelector((state) => state.core.axios);
    const [servicePriceDetails, setServicePriceDetails] =
        useState<ServicePriceDetails>();

    useEffect(() => {
        dispatch(LoadMarketplacePricing(props.engagementId));
    }, []);

    useEffect(() => {
        calculatePricing();
    }, [marketplacePricing]);

    const calculatePricing = () => {
        const pricing =
            PricesCalculationHelper.getBrokeragePricingByLevelOfService(
                props.levelOfService,
                marketplacePricing
            );
        const servicePrice = PricesCalculationHelper.getSuspensionFeePrice(
            pricing,
            props.startLtmRevenue,
            1
        );

        setServicePriceDetails(servicePrice);
    };

    const renderMonthlySuspentionAmount = () => {
        if (!marketplacePricing) {
            return <>Loading...</>;
        }

        const isCreditable =
            props.levelOfService === LevelOfService.DiscountBrokerage;

        return (
            <>
                {Helpers.GetCurrencyFormat(
                    servicePriceDetails?.totalPrice,
                    2,
                    true
                )}{' '}
                per month, (
                {isCreditable
                    ? `${servicePriceDetails?.creditablePercent}%`
                    : 'not'}{' '}
                creditable)
            </>
        );
    };

    const suspendEngagement = () => {
        setIsLoading(true);
        axios
            .post(`/api/Marketplace/Engagements/${props.engagementId}/Suspend`)
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An error occured during suspension',
                        'error'
                    );
                    setIsLoading(false);
                } else {
                    props.fetchData();
                }
            })
            .catch(() => {
                createNotification(
                    'An error occured during suspension',
                    'error'
                );
            })
            .finally(() => {
                setIsLoading(false);
                props.setIsVisible(false);
            });
    };

    return (
        <>
            <Rodal
                visible={props.isVisible}
                onClose={() => props.setIsVisible(false)}
                animation="slideUp"
                className="suspend-engagement-popup"
                showMask={true}
                width={600}
                showCloseButton={true}>
                <ModalHeader>Suspend Engagement</ModalHeader>
                <ModalBody>
                    <div>
                        <div className="main-text">
                            The engagement suspension period commences with the
                            day you suspend the engagement and ends with the day
                            you resume the engagement. During the engagement
                            suspension period:
                        </div>
                        <ul className="mb-20 mt-20">
                            <li>
                                <div>
                                    Your timeline and every buyer&apos;s
                                    timeline stop where they are: the steps
                                    under way are cancelled, no step starts,
                                    no deadlines run, and buyers see the
                                    engagement as Suspended
                                </div>
                            </li>
                            {props.isTheCatchUpRunning && (
                                <li>
                                    <div>
                                        The catch-up running now stops where it
                                        is, and the buyers on it stop too. When
                                        you resume, a new catch-up brings them
                                        back from where they had got to
                                    </div>
                                </li>
                            )}
                            <li>
                                <div>
                                    You will have read-only access to your
                                    engagement dashboard on VV Connect, and you
                                    will not be able advance in the engagement
                                    process
                                </div>
                            </li>
                            <li>
                                <div>
                                    No further work will be performed on your
                                    engagement by VetValue
                                </div>
                            </li>
                            {!isSfas(props.levelOfService) ? (
                                <>
                                    {props.levelOfService ===
                                        LevelOfService.FullServiceBrokerage && (
                                        <li>
                                            <div>
                                                Monthly Engagement Fees will not
                                                be charged
                                            </div>
                                        </li>
                                    )}
                                    <li>
                                        <div>
                                            {props.levelOfService ===
                                            LevelOfService.FullServiceBrokerage
                                                ? 'A monthly suspension fee will be charged on the next scheduled engagement fee date and on each monthly anniversary thereof during the course of the suspension'
                                                : 'A Monthly suspension fees will be charged upon the suspension of the engagement and on each monthly anniversary thereof during the course of the suspension'}
                                        </div>
                                    </li>
                                </>
                            ) : (
                                <></>
                            )}
                        </ul>
                        <div className="mb-20">
                            When you resume, a catch-up replays the engagement
                            from the first step for the buyers who had left.
                            Your timeline and the buyers still in the deal
                            continue from where they stopped once the catch-up
                            reaches them: the steps cancelled at the suspension
                            start again, with their full length.
                        </div>
                        {!isSfas(props.levelOfService) ? (
                            <div>
                                Monthly suspension fees for the engagement are:{' '}
                                {renderMonthlySuspentionAmount()}{' '}
                            </div>
                        ) : (
                            <></>
                        )}
                        <div className="suspend-buttons-section">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => props.setIsVisible(false)}>
                                Continue Engagement
                            </button>
                            <ButtonLoader
                                type="submit"
                                className={
                                    'btn btn-primary select-documents-btn'
                                }
                                disabled={isLoading}
                                isLoading={isLoading}
                                loaderButtonText={''}
                                buttonText={'Suspend Engagement'}
                                onClick={suspendEngagement}
                            />
                        </div>
                    </div>
                </ModalBody>
            </Rodal>
        </>
    );
};
